import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { useGameSpectators } from '../../store/spectators-store';
import { isGuestPlayer } from '../../utils/guest';
import { UserAvatar } from './UserAvatar';
import PlayerProfileOverlay from '../profile/PlayerProfileOverlay';
import './SpectatorsPanel.css';

/**
 * Chi sta guardando questa partita — il contenuto dell'occhio.
 *
 * Due ospiti, e due forme: da 900px in su è la SCHEDA degli spettatori nella
 * colonna, in mezzo a Chat e Mosse (GameSidebarTabs); sotto, dov'è una colonna
 * non c'è, resta il pannello a tutto schermo della barra in basso
 * (`mobilePanels`). Da qui non si vede la differenza: in entrambi i casi è un
 * figlio di una colonna flex che si prende lo spazio e scorre da sé.
 *
 * Il profilo sta DENTRO: il `profileFor` di GameScreen non è raggiungibile da
 * GameSidebarTabs, e `FullscreenPanel` tiene già una pila sua per Escape,
 * quindi anche nell'ospite mobile — dove il profilo si apre sopra un pannello —
 * si chiude da solo senza portarsi via quello sotto.
 */
export function SpectatorsPanel() {
  const { t } = useTranslation();
  const { spectators } = useGameSpectators();
  const [profileFor, setProfileFor] = useState<string | null>(null);

  if (spectators.length === 0) {
    return (
      <div className="spectators-panel spectators-panel--empty" data-testid="spectators-panel">
        <Eye size={28} aria-hidden="true" />
        <p>{t('spectate.watchers.empty')}</p>
      </div>
    );
  }

  return (
    <div className="spectators-panel" data-testid="spectators-panel">
      <ul className="spectators-list">
        {spectators.map((s) => {
          // Il profilo di un ospite è una pagina vuota, e per un bot rivelerebbe
          // il travestimento: stessa regola della lista dei collegati.
          const senzaProfilo = isGuestPlayer(s);
          const contenuto = (
            <>
              <UserAvatar username={s.username} avatar={s.avatar} variant="opponent" size="sm" />
              <span className="spectator-name">{s.username}</span>
            </>
          );

          return (
            <li key={s.userId} className="spectator-item">
              {senzaProfilo ? (
                <div className="spectator-row">{contenuto}</div>
              ) : (
                <button
                  type="button"
                  className="spectator-row spectator-row--link"
                  onClick={() => { setProfileFor(s.username); }}
                >
                  {contenuto}
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {profileFor && (
        <PlayerProfileOverlay username={profileFor} onClose={() => { setProfileFor(null); }} />
      )}
    </div>
  );
}

export default SpectatorsPanel;
